import type { ApiRoute } from '@socialfy/api-contract';
import type { z } from 'zod';
import { ApiContractError, ApiOffline, CsrfMissing, parseProblemBody } from './problem';
import type { InputArgs, QueryValue, ResponseData } from './types';

export type CallOptions = {
  signal?: AbortSignal;
  /** Schlüssel der Nutzerhandlung; ohne Angabe entsteht einer je Aufruf. */
  idempotencyKey?: string;
};

export type ApiClientConfig = {
  /** Vorangestellte Adresse (Standard: dieselbe Herkunft wie die Seite). */
  baseUrl?: string;
  fetch?: typeof fetch;
  /** Aktuelle Schutzmarke der Sitzung (aus `GET /me`), nie aus dem Browserspeicher. */
  getCsrfToken: () => string | null;
  /** Lädt die Sitzung neu (neue Schutzmarke); danach wiederholt der Client genau einmal. */
  onCsrfRejected?: () => Promise<void>;
  /** Wird bei jeder 401-Antwort aufgerufen (Weiterleitung zur Anmeldung). */
  onUnauthorized?: () => void;
};

type Params = Readonly<Record<string, string | number>>;
type Query = Readonly<Record<string, QueryValue | readonly string[]>>;

function queryText(value: QueryValue | readonly string[]): string | null {
  if (value === undefined) {
    return null;
  }
  if (typeof value === 'object') {
    return value.join(',');
  }
  return String(value);
}

/**
 * Baut den Pfad einer Route: `{name}` wird mit `encodeURIComponent` ersetzt (`a/b` wird `a%2Fb`), die Query
 * aus einem Objekt (Listen kommagetrennt, Wahrheitswerte als Text, undefinierte Werte entfallen).
 */
export function buildUrl(path: string, params?: Params, query?: Query): string {
  const filled = path.replaceAll(/\{([^}]+)\}/g, (_match, name: string) => {
    const value = params?.[name];
    if (value === undefined) {
      throw new TypeError(`Pfadparameter fehlt: ${name}`);
    }
    return encodeURIComponent(String(value));
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    const text = queryText(value);
    if (text !== null) {
      search.append(key, text);
    }
  }
  const suffix = search.toString();
  return suffix === '' ? filled : `${filled}?${suffix}`;
}

type ResponseDef = { content?: Record<string, { schema?: z.ZodType } | undefined> } | undefined;
type Input = { params?: Params; query?: Query; body?: unknown } | undefined;

const isMutating = (method: string): boolean => method !== 'get' && method !== 'head';

async function readJson(response: Response): Promise<{ ok: true; value: unknown } | { ok: false }> {
  const text = await response.text();
  if (text === '') {
    return { ok: false };
  }
  try {
    return { ok: true, value: JSON.parse(text) as unknown };
  } catch {
    return { ok: false };
  }
}

async function parseSuccess(
  operationId: string,
  responses: Record<string, ResponseDef>,
  response: Response,
): Promise<unknown> {
  const declared = responses[String(response.status)];
  if (declared === undefined) {
    throw new ApiContractError(operationId, [`Status ${response.status} ist nicht deklariert`]);
  }
  const schema = declared.content?.['application/json']?.schema;
  if (schema === undefined) {
    return undefined;
  }
  const body = await readJson(response);
  if (!body.ok) {
    throw new ApiContractError(operationId, ['Der Rumpf ist leer oder kein JSON']);
  }
  const parsed = schema.safeParse(body.value);
  if (!parsed.success) {
    throw new ApiContractError(
      operationId,
      parsed.error.issues.map((issue) => `${issue.path.join('.') || '(Wurzel)'}: ${issue.message}`),
    );
  }
  return parsed.data;
}

async function problemOf(response: Response) {
  const body = await readJson(response);
  return parseProblemBody(
    body.ok ? body.value : undefined,
    response.status,
    response.headers.get('retry-after'),
  );
}

/** Ein Abbruch (`AbortError`) ist kein Verbindungsfehler; die Klasse hängt von der Umgebung ab, darum der Name. */
const isAbort = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { name?: unknown }).name === 'AbortError';

/**
 * Typisierter Client für `ApiRoute`-Definitionen: Pfad, Query und JSON-Rumpf aus der Eingabe, CSRF-Marke und
 * Idempotenzschlüssel nach den Metadaten der Route, Antworten gegen das Schema der Route geprüft.
 * Netzwerkfehler werden zu `ApiOffline`, Problem-Antworten zu `ApiProblem`, Schemaverstöße zu `ApiContractError`.
 */
export function createApiClient(config: ApiClientConfig) {
  const fetchImpl: typeof fetch = config.fetch ?? ((input, init) => globalThis.fetch(input, init));
  const origin = () => config.baseUrl ?? globalThis.location?.href ?? 'http://localhost/';

  function requestFor(api: ApiRoute, input: Input, options: CallOptions | undefined, key?: string) {
    const { meta, route } = api;
    const headers = new Headers({ accept: 'application/json' });
    if (meta.csrf && isMutating(route.method)) {
      const token = config.getCsrfToken();
      if (token === null) {
        throw new CsrfMissing();
      }
      headers.set('x-csrf-token', token);
    }
    if (key !== undefined) {
      headers.set('idempotency-key', key);
    }
    const init: RequestInit = {
      method: route.method.toUpperCase(),
      headers,
      credentials: 'same-origin',
      ...(options?.signal === undefined ? {} : { signal: options.signal }),
    };
    if (input?.body !== undefined) {
      headers.set('content-type', 'application/json');
      init.body = JSON.stringify(input.body);
    }
    return init;
  }

  async function send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetchImpl(url, init);
    } catch (error) {
      throw isAbort(error) ? error : new ApiOffline();
    }
  }

  async function call<R extends ApiRoute>(
    api: R,
    ...args: [...InputArgs<R>, options?: CallOptions]
  ): Promise<ResponseData<R>> {
    const [input, options] = args as unknown as [Input, CallOptions | undefined];
    const { meta, route } = api;
    const url = new URL(buildUrl(route.path, input?.params, input?.query), origin()).toString();
    const key =
      meta.idempotency === 'key' ? (options?.idempotencyKey ?? crypto.randomUUID()) : undefined;

    // Höchstens ein zweiter Versuch, und nur nach abgelehnter Schutzmarke (gleicher Schlüssel).
    for (let attempt = 0; ; attempt++) {
      const response = await send(url, requestFor(api, input, options, key));
      if (response.ok) {
        const responses = route.responses as Record<string, ResponseDef>;
        return (await parseSuccess(
          `${meta.module}.${meta.name}`,
          responses,
          response,
        )) as ResponseData<R>;
      }
      const problem = await problemOf(response);
      if (problem.isCsrf && attempt === 0 && config.onCsrfRejected !== undefined) {
        await config.onCsrfRejected();
        continue;
      }
      if (response.status === 401) {
        config.onUnauthorized?.();
      }
      throw problem;
    }
  }

  return { call };
}

export type ApiClient = ReturnType<typeof createApiClient>;
